import { type MouseEvent } from "react";
import { PinIcon } from "lucide-react";

import { Button } from "@/components/ui/button";

import { usePinnedJobs } from "@/contexts/PinnedJobsContext";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

const ICON_CLASS =
  "shrink-0 p-0 text-lever-text-label opacity-0 hover:text-lever-text-information group-hover:text-lever-text-information group-hover:opacity-100";
const ICON_PINNED_CLASS =
  "text-lever-text-information opacity-100 hover:text-lever-text-information";

const LOGO_CLASS =
  "absolute inset-0 rounded-none bg-transparent p-0 text-lever-text-on-action opacity-0 hover:bg-lever-overlay-default/55 hover:text-lever-text-on-action group-hover/logo:bg-lever-overlay-default/55 group-hover/logo:opacity-100";
const LOGO_PINNED_CLASS =
  "bg-lever-overlay-default/35 text-lever-text-on-action opacity-100 hover:bg-lever-overlay-default/55 hover:text-lever-text-on-action";

const VARIANT_STYLES = {
  small: {
    button: "h-5 w-5",
    icon: "h-3.5 w-3.5",
    base: ICON_CLASS,
    pinned: ICON_PINNED_CLASS,
  },
  medium: {
    button: "h-6 w-6",
    icon: "h-4 w-4",
    base: ICON_CLASS,
    pinned: ICON_PINNED_CLASS,
  },
  logo: {
    button: "h-full w-full",
    icon: "h-4 w-4",
    base: LOGO_CLASS,
    pinned: LOGO_PINNED_CLASS,
  },
} as const;

export type JobPinButtonVariant = keyof typeof VARIANT_STYLES;

// Pins/unpins a job for the current user. Hidden until the nearest `group`
// ancestor is hovered, unless the job is already pinned.
export const JobPinButton = ({
  className,
  companyId,
  jobId,
  screen,
  variant = "small",
}: {
  className?: string;
  companyId: string;
  jobId: string;
  screen: MIXPANEL_SCREENS;
  variant?: JobPinButtonVariant;
}) => {
  /* Database CRUD Operations */
  // Read + write
  const { isPinned, togglePin } = usePinnedJobs();

  /* Helpers */
  const pinned = isPinned(jobId);
  const styles = VARIANT_STYLES[variant];

  /* Handlers */
  const handleTogglePin = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    mixpanelUtils.trackButtonClick(pinned ? "Unpin job" : "Pin job", screen, {
      job_id: jobId,
      company_id: companyId,
    });
    togglePin(jobId);
  };

  /* Render */
  const render = () => (
    <Button
      type="button"
      variant="ghost"
      onClick={handleTogglePin}
      className={cn(
        styles.base,
        styles.button,
        pinned && styles.pinned,
        className,
      )}
    >
      <PinIcon className={cn(styles.icon, pinned && "fill-current")} />
    </Button>
  );

  return render();
};
